import { mods } from '../data/mods';
import { Stat, Item, Idol } from '../types';
import { Mod, IdolSize } from '../types/mods';
import { ITEM_TYPES } from '../constants';

// Import these functions directly from IdolPasteHandler.tsx
export const extractIdolSize = (itemName: string, itemRarity: string): IdolSize | null => {
    if (itemRarity === 'Unique') {
        return IdolSize.UNIQUE;
      }
    
      const sizePatterns: Array<{ pattern: RegExp; size: IdolSize }> = [
        { pattern: /Minor Idol/i, size: IdolSize.MINOR },
        { pattern: /Kamasan Idol/i, size: IdolSize.KAMASAN },
        { pattern: /Totemic Idol/i, size: IdolSize.TOTEMIC },
        { pattern: /Conqueror Idol/i, size: IdolSize.CONQUEROR },
        { pattern: /Noble Idol/i, size: IdolSize.NOBLE },
        { pattern: /Burial Idol/i, size: IdolSize.BURIAL },
      ];
    
      for (const { pattern, size } of sizePatterns) {
        if (pattern.test(itemName)) {
          return size;
        }
      }
    
      return null;
};

export const extractNumericValues = (modText: string): number[] => {
    const matches = modText.match(/([+-]?\d+(?:\.\d+)?)/g);
    if (!matches) return [];
    return matches.map(value => parseFloat(value));
};

export const findMatchingMod = (modText: string, idolSize: IdolSize): {mod: Mod; value: number | null; score: number} | null => {
  // Extract numeric values
  const numericValues = extractNumericValues(modText);

  // Create tokens for the input mod
  const inputTokens = tokenizeMod(modText);

  // Get all mods for this idol size
  const potentialMods = Object.values(mods).filter(mod => mod.idolSizes.includes(idolSize));

  if (potentialMods.length === 0) {
    console.warn(`No mods found for idol size ${idolSize}`);
    return null;
  }

  // Score each potential mod by similarity
  const scoredMods = potentialMods.map(mod => {
    const dbTokens = tokenizeMod(mod.description);

    // Start with a base score
    let score = 0;

    // Content type match is a strong signal
    if (inputTokens.contentType && inputTokens.contentType === dbTokens.contentType) {
      score += 30;
    }

    // Subject match is also very important
    if (inputTokens.subject && inputTokens.subject === dbTokens.subject) {
      score += 30;
    }

    // Action match helps differentiate similar mods
    if (inputTokens.action && dbTokens.action &&
      (inputTokens.action.includes(dbTokens.action) ||
        dbTokens.action.includes(inputTokens.action))) {
      score += 20;
    }

    // Compare the segments for structural similarity
    const segmentScore = calculateSegmentSimilarity(inputTokens.segments, dbTokens.segments);
    score += segmentScore * 20;

    // Full text similarity as a fallback
    const textSimilarity = calculateTextSimilarity(inputTokens.normalized, dbTokens.normalized);
    score += textSimilarity * 10;

    // Check if words after the numeric parts are similar
    // This helps differentiate mods with similar beginnings but different effects
    const afterNumericSimilarity = compareAfterNumeric(
      inputTokens.normalized,
      dbTokens.normalized,
      inputTokens.numericPositions,
      dbTokens.numericPositions
    );
    score += afterNumericSimilarity * 20;

    // If the content types and subjects match exactly, this is probably the right mod
    // Check for exact match after normalizing the numbers
    const strippedInput = stripNumbers(inputTokens.normalized);
    const strippedDb = stripNumbers(dbTokens.normalized);

    if (strippedInput === strippedDb) {
      score += 50; // Strong bonus for exact match after stripping numbers
    }

    // Special handling for numeric values in specific contexts
    const inputNumerics = extractNumericContext(inputTokens.normalized);
    const dbNumerics = extractNumericContext(dbTokens.normalized);

    // Compare numbers in similar contexts
    for (const inputNum of inputNumerics) {
      // Skip the first number as it's usually the variable value of the mod
      if (inputNum.position === inputTokens.numericPositions[0]) continue;

      // Look for DB numbers with similar context
      for (const dbNum of dbNumerics) {
        // If contexts are similar but numbers differ, penalize the score
        if (calculateTextSimilarity(inputNum.context, dbNum.context) > 0.6 &&
          inputNum.number !== dbNum.number) {
          score -= 40; // Strong penalty for mismatched numbers in similar contexts
        }
      }
    }

    return {
      mod,
      score,
      value: numericValues.length > 0 ? numericValues[0] : null
    };
  });

  // Sort by score descending
  scoredMods.sort((a, b) => b.score - a.score);

  // Accept a match if it scores high enough or is significantly better than alternatives
  if (scoredMods.length > 0) {
    const bestMatch = scoredMods[0];

    // Accept match if:
    // 1. It's a very strong match (score > 70), or
    // 2. It's the best match by far (score > 40 and significantly better than second best)
    const secondBestScore = scoredMods.length > 1 ? scoredMods[1].score : 0;

    if (bestMatch.score > 60 ||
      (bestMatch.score > 40 && bestMatch.score > secondBestScore * 1.15)) {
      return bestMatch;
    }
  }

  console.warn(`No confident match found for: ${modText}`);
  return null;
};

// New function to convert PoE API Item to Idol type
export const convertItemToIdol = (item: Item, stashName: string): {idol: Idol | null; error?: string} => {
  const typeLine = item.typeLine.toLowerCase();
  const name = item.name || item.typeLine;
  const rarity = item.rarity || (item.frameType !== undefined ? 
    ["Normal", "Magic", "Rare", "Unique"][item.frameType] || "Normal" : "Normal");
  
  // Use existing function to determine idol size
  const idolSizeEnum = extractIdolSize(typeLine, rarity);
  if (!idolSizeEnum) {
    return { idol: null, error: `Could not determine idol size from: ${typeLine}` };
  }
  
  // Map to correct item type from constants
  let itemType;
  if (idolSizeEnum === IdolSize.UNIQUE) {
    itemType = ITEM_TYPES.UNIQUE;
  } else {
    // Get the corresponding item type
    const itemTypeKey = Object.keys(ITEM_TYPES).find(key => 
      ITEM_TYPES[key].name.toUpperCase().includes(idolSizeEnum.toString().toUpperCase())
    );
    itemType = itemTypeKey ? ITEM_TYPES[itemTypeKey] : null;
  }
  
  if (!itemType) {
    return { idol: null, error: `Could not find item type for idol size: ${idolSizeEnum}` };
  }
  
  // Parse explicit mods
  const stats: Stat[] = [];
  if (item.explicitMods) {
    const usedModIds = new Set<number>();
    
    for (const modText of item.explicitMods) {
      const matchResult = findMatchingMod(modText, idolSizeEnum);
      
      if (matchResult && !usedModIds.has(matchResult.mod.id)) {
        stats.push({
          explicitType: matchResult.mod.type === 'prefix' ? "prefix" : "suffix",
          value: matchResult.value !== null ? matchResult.value : 0,
          contentType: matchResult.mod.contentType,
          modId: matchResult.mod.id,
        });
        
        usedModIds.add(matchResult.mod.id);
      }
    }
  }
  
  // Create unique ID
  const uniqueID = `poe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  const idol = {
    id: uniqueID,
    name,
    size: itemType,
    stats,
    stashTabName: stashName,
    stashPosition: {
      x: item.x || 0,
      y: item.y || 0
    }
  };
  
  return { idol };
};

export const tokenizeMod = (description: string) => {
  const normalized = description
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

  // Find positions of all numbers to help with segmentation
  const numericMatches = Array.from(normalized.matchAll(/[+-]?\d+(?:\.\d+)?%?|\([^)]+\)%?/g));
  const numericPositions = numericMatches.map(match => match.index || 0);

  // Identify the content type
  const contentTypes = [
    "abyss", "delirium", "breach", "betrayal", "harvest", "ritual", "legion",
    "expedition", "essence", "blight", "heist", "incursion", "beyond", "delve",
    "harbinger", "ultimatum", "anarchy", "bestiary", "boss", "shrine", "map",
    "torment", "scarab", "ambush", "vaal"
  ];

  let contentType: string | null = null;
  for (const type of contentTypes) {
    if (normalized.includes(type)) {
      contentType = type;
      break;
    }
  }

  // Extract the subject (what's being modified) - typically at the start of the mod
  // Examples: "Your Maps", "Abyss Cracks", "Legion Encounters"
  let subject: string | null = null;

  // Common subject patterns
  const subjectPatterns = [
    /^(your maps)(?= have| contain)/i,
    /^(.*?(?:cracks|troves|spires|monsters|altars|encounters|bosses|spirits|chests|veins|shrines))(?= in your maps| have)/i,
  ];

  for (const pattern of subjectPatterns) {
    const match = normalized.match(pattern);
    if (match && match[1]) {
      subject = match[1].trim();
      break;
    }
  }

  // Try to identify the main action of the mod
  // Like "have chance to contain", "increased", "have chance to spawn", etc.
  let action: string | null = null;

  const actionPatterns = [
    /have (?:\+|\(.*?\))?\s?(?:\d+(?:\.\d+)?%?)?\s?chance to contain/i,
    /have (?:\+|\(.*?\))?\s?(?:\d+(?:\.\d+)?%?)?\s?chance to spawn/i,
    /have (?:\+|\(.*?\))?\s?(?:\d+(?:\.\d+)?%?)?\s?increased chance/i,
    /(?:\d+(?:\.\d+)?%?)?\s?increased/i,
    /(?:\d+(?:\.\d+)?%?)?\s?more/i,
    /(?:\d+(?:\.\d+)?%?)?\s?chance to/i,
    /contain an additional/i,
  ];

  for (const pattern of actionPatterns) {
    const match = normalized.match(pattern);
    if (match) {
      action = match[0].trim();
      break;
    }
  }

  // Break the mod into meaningful segments
  // This helps compare the overall structure of the mod
  const segments = normalized
    // Replace numbers with placeholders to focus on the text structure
    .replace(/\+?(\d+(?:\.\d+)?)%?/g, 'NUMBER')
    .replace(/\(\d+(?:\.\d+)?–\d+(?:\.\d+)?\)%?/g, 'RANGE')
    // Split on common delimiters keeping the structure
    .split(/\b(in your maps|have|chance to|increased|more|additional)\b/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  return {
    contentType,
    subject,
    action,
    normalized,
    segments,
    numericPositions
  };
};

export const calculateSegmentSimilarity = (segments1: string[], segments2: string[]) => {
  if (segments1.length === 0 || segments2.length === 0) {
    return 0;
  }

  // Check for exact segment match first
  if (segments1.join('|') === segments2.join('|')) {
    return 1.0;
  }

  // Count matching segments
  let matches = 0;
  for (const seg1 of segments1) {
    if (segments2.includes(seg1)) {
      matches++;
    }
  }

  // Jaccard similarity
  return matches / (segments1.length + segments2.length - matches);
};

export const calculateTextSimilarity = (text1: string, text2: string) => {
  const words1 = text1.split(/\s+/).filter(w => w.length > 3);
  const words2 = text2.split(/\s+/).filter(w => w.length > 3);

  if (words1.length === 0 || words2.length === 0) {
    return 0;
  }

  const commonWords = words1.filter(w => words2.includes(w));
  return commonWords.length / (words1.length + words2.length - commonWords.length);
};

export const compareAfterNumeric = (input: string, db: string, inputPositions: number[], dbPositions: number[]) => {
  if (inputPositions.length === 0 || dbPositions.length === 0) {
    return 0;
  }

  try {
    // Get text after the first numeric value
    const inputAfter = input.slice(inputPositions[0] + 1).trim();
    const dbAfter = db.slice(dbPositions[0] + 1).trim();

    // Calculate word-level Jaccard similarity
    const inputWords = inputAfter.split(/\s+/).filter(w => w.length > 3);
    const dbWords = dbAfter.split(/\s+/).filter(w => w.length > 3);

    if (inputWords.length === 0 || dbWords.length === 0) {
      return 0;
    }

    const commonWords = inputWords.filter(w => dbWords.includes(w));
    return commonWords.length / (inputWords.length + dbWords.length - commonWords.length);
  } catch (error) {
    console.error("Error in compareAfterNumeric:", error);
    return 0;
  }
};

export const stripNumbers = (text: string) => {
  return text
    .replace(/\+?(\d+(?:\.\d+)?)%?/g, 'NUMBER')
    .replace(/\(\d+(?:\.\d+)?–\d+(?:\.\d+)?\)%?/g, 'RANGE')
    .replace(/\s+/g, ' ')
    .trim();
};

export const extractNumericContext = (text: string) => {
  const result: { number: number; context: string; position: number }[] = [];

  // Find all numbers in the text
  const matches = Array.from(text.matchAll(/(\d+(?:\.\d+)?)/g));

  for (const match of matches) {
    if (match.index !== undefined) {
      // Get surrounding context (5 chars before and after)
      const start = Math.max(0, match.index - 5);
      const end = Math.min(text.length, match.index + match[0].length + 5);
      const context = text.substring(start, end);

      result.push({
        number: parseFloat(match[0]),
        context,
        position: match.index
      });
    }
  }

  return result;
};